import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import { CategoryChips } from "../components/CategoryChips";
import { FoodCostBadge } from "../components/FoodCostBadge";
import { TrafficLight } from "../components/TrafficLight";
import { FABWithMenu } from "../components/FABWithMenu";
import { RecipeCatalogDialog } from "../components/RecipeCatalogDialog";
import { OrderRecipeButton } from "../components/OrderRecipeButton";
import { AssociateRecipeToSupplierButton } from "../components/AssociateSupplierButtons";
import MagicChefButton from "../components/MagicChefButton";
import RestaurantMenuBuilderDialog from "../components/RestaurantMenuBuilderDialog";
import { RecipeDetailDialog } from "../components/RecipeDetailDialog";
import { MenuImportDialog } from "../components/MenuImportDialog";
import { QuotaBadge } from "../components/QuotaBadge";
import { api } from "../lib/api";
import { RECIPE_CATEGORIES, euro } from "../lib/constants";
import { Search, X, BookOpen, ChefHat, Upload, Trash2, AlertTriangle, Euro, FileText } from "lucide-react";
import { toast } from "sonner";

export default function Recipes() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [cat, setCat] = useState("");
    const [catalogOpen, setCatalogOpen] = useState(false);
    const [detailRecipe, setDetailRecipe] = useState(null);
    const [ingredientsById, setIngredientsById] = useState({});
    const [menuImportOpen, setMenuImportOpen] = useState(false);
    const [restaurantMenuOpen, setRestaurantMenuOpen] = useState(false);
    const [deleteAllConfirm, setDeleteAllConfirm] = useState(false);
    const navigate = useNavigate();

    const reload = () => {
        setLoading(true);
        api.get("/recipes")
            .then(({ data }) => setItems(data))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        reload();
        api.get("/ingredients").then(({ data }) => {
            const map = {};
            (data || []).forEach((i) => { map[i.id] = i; });
            setIngredientsById(map);
        });
    }, []);

    const filtered = useMemo(() => {
        let r = items;
        if (cat) r = r.filter((i) => i.category === cat);
        if (q.trim()) {
            const s = q.toLowerCase();
            r = r.filter((i) => i.name.toLowerCase().includes(s));
        }
        return r;
    }, [items, q, cat]);

    const deleteAll = async () => {
        try {
            const { data } = await api.delete("/recipes/all");
            toast.success(`${data.deleted} piatti cancellati`);
            setDeleteAllConfirm(false);
            reload();
        } catch (e) {
            toast.error("Errore nella cancellazione");
        }
    };

    return (
        <AppShell>
            <PageHeader
                eyebrow="Menu"
                title="Piatti"
                subtitle="Costi, margini e prezzi consigliati per ogni piatto."
                right={
                    <div className="hidden lg:flex items-center gap-2">
                        <QuotaBadge kind="menus" label="Menu" />
                        <QuotaBadge kind="recipes" label="Ricette AI" />
                        <MagicChefButton />
                        {items.length > 0 && (
                            <button
                                type="button"
                                onClick={() => setDeleteAllConfirm(true)}
                                data-testid="recipes-delete-all-btn"
                                title="Cancella tutti i piatti"
                                className="inline-flex items-center gap-2 rounded-full border border-red-200 text-brand-danger bg-white px-4 py-2 text-sm font-semibold hover:bg-red-50 transition"
                            >
                                <Trash2 size={14} /> Cancella tutti
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={() => setRestaurantMenuOpen(true)}
                            data-testid="recipes-restaurant-menu-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-navy/30 text-brand-navy bg-white px-4 py-2 text-sm font-semibold hover:bg-brand-navy hover:text-white transition"
                        >
                            <FileText size={14} /> Genera Menù Ristorante
                        </button>
                        <button
                            type="button"
                            onClick={() => setMenuImportOpen(true)}
                            data-testid="recipes-import-menu-btn"
                            className="inline-flex items-center gap-2 rounded-full border border-brand-gold/60 text-brand-navy bg-brand-gold/10 px-4 py-2 text-sm font-semibold hover:bg-brand-gold/20 transition"
                        >
                            <Upload size={14} /> Carica menu PDF
                        </button>
                        <button
                            type="button"
                            onClick={() => setCatalogOpen(true)}
                            data-testid="recipes-catalog-header-btn"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white px-5 py-2.5 text-sm font-semibold hover:bg-brand-navyLight transition shadow-card"
                        >
                            <BookOpen size={14} /> Scegli dal catalogo
                        </button>
                    </div>
                }
            />

            <div className="flex flex-col sm:flex-row gap-3 mb-5">
                <div className="relative flex-1">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-brand-muted" />
                    <input
                        data-testid="recipes-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Cerca un piatto…"
                        className="w-full pl-9 pr-9 py-2.5 rounded-lg bg-white border border-brand-line text-sm focus:outline-none focus:border-brand-gold transition"
                    />
                    {q && (
                        <button onClick={() => setQ("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-muted">
                            <X size={14} />
                        </button>
                    )}
                </div>
            </div>

            <CategoryChips value={cat} onChange={setCat} categories={RECIPE_CATEGORIES} allLabel="Tutte" />

            <div className="mt-6 text-sm text-brand-muted">
                {loading ? "Caricamento…" : `${filtered.length} piatti`}
            </div>

            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="recipes-list">
                {filtered.map((r) => (
                    <div
                        key={r.id}
                        data-testid={`recipe-card-${r.id}`}
                        className="bg-paper rounded-xl border border-brand-line shadow-card hover:shadow-cardHover hover:border-brand-gold/40 transition-all duration-300 p-5 relative"
                    >
                        <button
                            type="button"
                            onClick={() => navigate(`/recipes/${r.id}`)}
                            className="text-left w-full"
                            aria-label={`Apri ${r.name}`}
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <div className="eyebrow text-brand-gold mb-1.5">{r.category}</div>
                                    <div className="flex items-center gap-2.5">
                                        <TrafficLight
                                            pct={r.food_cost_pct}
                                            hasPrice={r.selling_price > 0}
                                            size={14}
                                        />
                                        <h3 className="font-display text-2xl text-brand-ink leading-tight truncate">{r.name}</h3>
                                    </div>
                                    {r.notes && (
                                        <p className="mt-1.5 text-[12px] text-brand-muted italic font-display line-clamp-2">
                                            {r.notes}
                                        </p>
                                    )}
                                    {r.missing_ingredients > 0 && (
                                        <div
                                            className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-red-50 border border-red-200 text-brand-danger text-[11px] font-bold"
                                            title={
                                                r.orphan_ingredient_names?.length
                                                    ? `Escluse dal food cost: ${r.orphan_ingredient_names.join(", ")}`
                                                    : "Alcune righe non contribuiscono al food cost"
                                            }
                                            data-testid={`recipe-card-orphan-${r.id}`}
                                        >
                                            <AlertTriangle size={12} strokeWidth={2.4} />
                                            {r.missing_ingredients} {r.missing_ingredients === 1 ? "riga esclusa" : "righe escluse"} dal food cost
                                        </div>
                                    )}
                                    {r.zero_cost_ingredient_names?.length > 0 && (
                                        <div
                                            className="mt-2 ml-1.5 inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-[11px] font-bold"
                                            title={`Ingredienti senza prezzo: ${r.zero_cost_ingredient_names.join(", ")}. Il food cost risulta sottostimato finché non imposti un costo.`}
                                            data-testid={`recipe-card-zero-cost-${r.id}`}
                                        >
                                            <Euro size={12} strokeWidth={2.4} />
                                            {r.zero_cost_ingredient_names.length} senza prezzo
                                        </div>
                                    )}
                                </div>
                                <FoodCostBadge pct={r.food_cost_pct} size="lg" />
                            </div>
                            <div className="mt-5 pt-4 border-t border-brand-line grid grid-cols-3 gap-2 text-center">
                                <Metric label="Costo/porz" value={euro(r.cost_per_portion)} />
                                <Metric label="Prezzo" value={euro(r.selling_price)} />
                                <Metric label="Margine" value={euro(r.margin)} highlight={r.margin >= 0 ? "success" : "danger"} />
                            </div>
                        </button>
                        <div className="mt-4 pt-3 border-t border-brand-line/60 flex items-center justify-between gap-3 flex-wrap">
                            <div className="text-[11px] text-brand-muted">
                                Base: <strong>{r.portions || 1}</strong> porzioni
                            </div>
                            <div className="flex items-center gap-2">
                                {r.category !== "Bevande" && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setDetailRecipe(r);
                                        }}
                                        data-testid={`recipe-detail-btn-${r.id}`}
                                        className="inline-flex items-center gap-1.5 rounded-full border border-brand-line text-brand-navy bg-paper text-[12px] font-bold px-3 py-1.5 hover:border-brand-gold/60 hover:bg-brand-cream transition"
                                    >
                                        <BookOpen size={12} /> Ricetta dettagliata
                                    </button>
                                )}
                                <AssociateRecipeToSupplierButton recipe={r} />
                                <OrderRecipeButton recipe={r} />
                            </div>
                        </div>
                    </div>
                ))}
                {!loading && filtered.length === 0 && (
                    <div className="md:col-span-2 bg-paper rounded-xl border border-dashed border-brand-line p-10 text-center">
                        {items.length === 0 ? (
                            <div className="space-y-5" data-testid="recipes-empty-state">
                                <div className="mx-auto h-14 w-14 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center">
                                    <ChefHat size={24} />
                                </div>
                                <div>
                                    <div className="font-display text-2xl text-brand-ink">Il tuo menu è vuoto</div>
                                    <p className="text-sm text-brand-muted mt-1.5 max-w-sm mx-auto">
                                        Inizia scegliendo un piatto dal nostro catalogo di oltre 100 ricette italiane,
                                        oppure crea la tua prima ricetta da zero.
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                                    <button
                                        type="button"
                                        onClick={() => setCatalogOpen(true)}
                                        data-testid="recipes-empty-pick-btn"
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-gold text-brand-navy font-semibold text-sm hover:bg-brand-goldDark transition-colors"
                                    >
                                        <BookOpen size={16} /> Scegli dal catalogo
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/recipes/new")}
                                        data-testid="recipes-empty-create-btn"
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-brand-navy text-white font-semibold text-sm hover:bg-brand-navyLight transition-colors"
                                    >
                                        <ChefHat size={16} /> Crea piatto
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="text-sm text-brand-muted">Nessun piatto trovato.</div>
                        )}
                    </div>
                )}
            </div>

            <FABWithMenu
                onCreate={() => navigate("/recipes/new")}
                onPickCatalog={() => setCatalogOpen(true)}
                testid="recipes-fab"
            />

            <RecipeCatalogDialog
                open={catalogOpen}
                onOpenChange={setCatalogOpen}
                onAdded={() => {
                    reload();
                }}
            />

            {detailRecipe && (
                <RecipeDetailDialog
                    recipe={detailRecipe}
                    ingredientsById={ingredientsById}
                    onClose={() => setDetailRecipe(null)}
                />
            )}

            <MenuImportDialog
                open={menuImportOpen}
                onClose={() => setMenuImportOpen(false)}
                onImported={reload}
            />

            <RestaurantMenuBuilderDialog
                open={restaurantMenuOpen}
                onOpenChange={setRestaurantMenuOpen}
            />

            {deleteAllConfirm && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 backdrop-blur-sm px-4"
                    onClick={() => setDeleteAllConfirm(false)}
                    data-testid="recipes-delete-all-dialog"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="w-full max-w-md bg-paper rounded-2xl shadow-card border border-brand-line overflow-hidden"
                    >
                        <div className="px-6 py-5 border-b border-brand-line">
                            <div className="h-11 w-11 rounded-full bg-red-50 text-brand-danger flex items-center justify-center mb-3">
                                <Trash2 size={20} />
                            </div>
                            <h3 className="font-display text-2xl text-brand-ink">
                                Cancellare tutti i piatti?
                            </h3>
                            <p className="mt-2 text-sm text-brand-muted">
                                Verranno eliminati <strong>{items.length}</strong> piatti dal tuo menu.
                                Gli ingredienti NON verranno cancellati. Questa azione non si può annullare.
                            </p>
                        </div>
                        <div className="px-6 py-4 bg-brand-cream flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setDeleteAllConfirm(false)}
                                data-testid="recipes-delete-all-cancel"
                                className="px-4 py-2 rounded-full text-sm font-semibold text-brand-muted hover:text-brand-ink transition"
                            >
                                Annulla
                            </button>
                            <button
                                type="button"
                                onClick={deleteAll}
                                data-testid="recipes-delete-all-confirm"
                                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-brand-danger text-white text-sm font-bold hover:opacity-90 transition"
                            >
                                <Trash2 size={14} /> Sì, cancella tutto
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}

function Metric({ label, value, highlight }) {
    const color = highlight === "success" ? "text-brand-success" : highlight === "danger" ? "text-brand-danger" : "text-brand-ink";
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider text-brand-muted font-semibold">{label}</div>
            <div className={`mt-0.5 text-sm font-semibold ${color}`}>{value}</div>
        </div>
    );
}
